import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MapPin, Search, ChevronRight, Sparkles, Building2, Home } from 'lucide-react';
import neighborhoodMap, { AreaInfo } from '../../data/neighborhoodMap';
import { motion } from 'framer-motion';

interface AreaCardProps {
  areaSlug: string;
  areaName: string;
  county: string;
  featured?: boolean;
}

const DUBLIN_DISTRICT_AREAS: AreaInfo[] = [
  { areaSlug: 'dublin-1', areaName: 'Dublin 1', county: 'Dublin City' },
  { areaSlug: 'dublin-2', areaName: 'Dublin 2', county: 'Dublin City' },
  { areaSlug: 'dublin-3', areaName: 'Dublin 3', county: 'Dublin City' },
  { areaSlug: 'dublin-4', areaName: 'Dublin 4', county: 'Dublin City' },
  { areaSlug: 'dublin-5', areaName: 'Dublin 5', county: 'Dublin City' },
  { areaSlug: 'dublin-6', areaName: 'Dublin 6', county: 'Dublin City' },
  { areaSlug: 'dublin-7', areaName: 'Dublin 7', county: 'Dublin City' },
  { areaSlug: 'dublin-8', areaName: 'Dublin 8', county: 'Dublin City' },
  { areaSlug: 'dublin-9', areaName: 'Dublin 9', county: 'Dublin City' },
  { areaSlug: 'dublin-10', areaName: 'Dublin 10', county: 'Dublin City' },
  { areaSlug: 'dublin-11', areaName: 'Dublin 11', county: 'Dublin City' },
  { areaSlug: 'dublin-12', areaName: 'Dublin 12', county: 'Dublin City' },
  { areaSlug: 'dublin-13', areaName: 'Dublin 13', county: 'Dublin City' },
  { areaSlug: 'dublin-14', areaName: 'Dublin 14', county: 'Dublin City' },
  { areaSlug: 'dublin-15', areaName: 'Dublin 15', county: 'Dublin City' },
  { areaSlug: 'dublin-16', areaName: 'Dublin 16', county: 'Dublin City' },
  { areaSlug: 'dublin-17', areaName: 'Dublin 17', county: 'Dublin City' },
  { areaSlug: 'dublin-18', areaName: 'Dublin 18', county: 'Dublin City' },
  { areaSlug: 'dublin-19', areaName: 'Dublin 19', county: 'Dublin City' },
  { areaSlug: 'dublin-20', areaName: 'Dublin 20', county: 'Dublin City' },
  { areaSlug: 'dublin-21', areaName: 'Dublin 21', county: 'Dublin City' },
  { areaSlug: 'dublin-22', areaName: 'Dublin 22', county: 'Dublin City' },
];

const AreaCard: React.FC<AreaCardProps> = ({ areaSlug, areaName, county, featured = false }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25 }}
      className="w-full"
    >
      <button
        className={`group flex min-h-[148px] w-full flex-col rounded-3xl border p-6 text-left transition-all duration-300 focus:outline-none focus:ring-4 no-underline ${
          featured
            ? 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50 hover:border-emerald-300 hover:shadow-xl focus:ring-emerald-200/60'
            : 'border-slate-200 bg-white hover:border-sky-300 hover:shadow-xl focus:ring-sky-200/60'
        }`}
        onClick={() => navigate(`/living-in/${areaSlug}`)}
        aria-label={`Discover ${areaName}, ${county}`}
      >
        <div className="flex items-start justify-between">
          <MapPin size={20} className={featured ? 'mb-4 text-emerald-600' : 'mb-4 text-slate-500'} />
          <ChevronRight
            size={20}
            className="text-slate-400 transition-transform group-hover:translate-x-1"
          />
        </div>

        <div className="mb-1 text-xl font-black leading-tight text-slate-900">{areaName}</div>
        <div className="mt-auto text-sm font-bold text-slate-600">{county}</div>
      </button>
    </motion.div>
  );
};

const DiscoverAreasPage: React.FC = () => {
  const [search, setSearch] = useState('');

  const allAreas = useMemo(() => {
    const merged = [...DUBLIN_DISTRICT_AREAS, ...neighborhoodMap.getAllAreas()];
    const deduped = new Map<string, AreaInfo>();
    merged.forEach((area) => {
      if (!deduped.has(area.areaSlug)) {
        deduped.set(area.areaSlug, area);
      }
    });
    return Array.from(deduped.values()).sort((a, b) => a.areaName.localeCompare(b.areaName));
  }, []);

  const filteredAreas = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allAreas;
    return allAreas.filter(
      (area) =>
        area.areaName.toLowerCase().includes(q) ||
        area.county.toLowerCase().includes(q) ||
        area.areaSlug.toLowerCase().includes(q)
    );
  }, [allAreas, search]);

  const filteredDublinDistricts = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return DUBLIN_DISTRICT_AREAS;
    return DUBLIN_DISTRICT_AREAS.filter(
      (area) =>
        area.areaName.toLowerCase().includes(q) ||
        area.county.toLowerCase().includes(q) ||
        area.areaSlug.toLowerCase().includes(q)
    );
  }, [search]);

  const canonicalUrl = 'https://rentsmart.ie/discover-areas';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>RentSmart: Find & Discover Rental Areas in Ireland</title>
        <meta
          name="description"
          content="Explore RentSmart living guides across Ireland, including new Dublin 1 to Dublin 22 area pages."
        />
        <meta
          name="keywords"
          content="living in dublin 1, living in dublin 22, ireland area guides, discover neighborhoods ireland"
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="RentSmart: Find & Discover Rental Areas in Ireland" />
        <meta
          property="og:description"
          content="Browse living guides for Irish neighborhoods and new Dublin district pages."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-20 px-4 relative overflow-hidden">
        <div className="absolute -top-10 -right-8 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <Sparkles className="h-4 w-4" /> Area Discovery Hub
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            Discover Rental Areas in Ireland
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed mb-7">
            Compare lifestyle fit, housing reality, commute patterns, and local character across Irish
            areas, now including dedicated Dublin 1 to Dublin 22 pages.
          </p>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur p-5 md:p-7">
            <div className="relative max-w-3xl">
              <Search
                size={20}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search area, county, or slug (e.g. Dublin 8, Meath, Tallaght)"
                className="w-full rounded-xl border border-white/20 bg-white py-3.5 pl-12 pr-4 text-lg text-slate-900 shadow-lg transition-shadow duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-300/50"
                aria-label="Search areas"
              />
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-3xl">
            <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest font-black text-slate-300 mb-1">Total Guides</p>
              <p className="text-2xl font-black text-white">{allAreas.length}</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest font-black text-slate-300 mb-1">Dublin Districts</p>
              <p className="text-2xl font-black text-white">22</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest font-black text-slate-300 mb-1">Search Results</p>
              <p className="text-2xl font-black text-white">{filteredAreas.length}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10 space-y-10">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-emerald-600" />
            <h2 className="text-2xl font-black text-slate-900">Dublin 1-22 Living Guides</h2>
          </div>
          <p className="text-sm font-semibold text-slate-600 mb-5">
           Dublin districts with dedicated living profile content.
          </p>

          {filteredDublinDistricts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredDublinDistricts.map((area) => (
                <AreaCard
                  key={area.areaSlug}
                  areaSlug={area.areaSlug}
                  areaName={area.areaName}
                  county={area.county}
                  featured
                />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center">
              <MapPin size={34} className="mx-auto mb-3 text-slate-400" />
              <p className="text-lg font-bold text-slate-800">No Dublin district matches your search</p>
            </div>
          )}
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Home className="h-5 w-5 text-sky-600" />
            <h2 className="text-2xl font-black text-slate-900">All Area Guides</h2>
          </div>

          {filteredAreas.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredAreas.map((area) => (
                <AreaCard
                  key={area.areaSlug}
                  areaSlug={area.areaSlug}
                  areaName={area.areaName}
                  county={area.county}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-16 text-center">
              <MapPin size={40} className="mx-auto mb-4 text-slate-400" />
              <p className="mb-2 text-xl font-black text-slate-900">No areas found for "{search}"</p>
              <p className="text-slate-600 font-semibold">Try a county name, a Dublin district, or a neighborhood slug.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default DiscoverAreasPage;
